import React, { useEffect, useState } from "react";
import { fechaHoraMX } from "@/lib/format";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ArrowLeft, Copy, ChevronUp, ChevronDown, Ban, ShieldCheck, X } from "lucide-react";
import DocumentosSection from "@/components/DocumentosSection";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

const fmtAcceso = (iso) => {
  return fechaHoraMX(iso);
};

// Muestra solo primer nombre + primer apellido (primeras dos palabras).
const nombreCorto = (n) => (n || "").trim().split(/\s+/).slice(0, 2).join(" ");

const ESTADOS_MX = [
  "Aguascalientes", "Baja California", "Baja California Sur", "Campeche", "Chiapas", "Chihuahua",
  "Ciudad de México", "Coahuila", "Colima", "Durango", "Estado de México", "Guanajuato", "Guerrero",
  "Hidalgo", "Jalisco", "Michoacán", "Morelos", "Nayarit", "Nuevo León", "Oaxaca", "Puebla",
  "Querétaro", "Quintana Roo", "San Luis Potosí", "Sinaloa", "Sonora", "Tabasco", "Tamaulipas",
  "Tlaxcala", "Veracruz", "Yucatán", "Zacatecas",
];

const EMPTY = { full_name: "", username: "", email: "", password: "", rol: "vendedor", celular: "", numero_identificacion: "", permite_sin_documentos: false };

// Formatea el ID de usuario a 2 dígitos (ej. "1" -> "01", "001" -> "01").
const fmtId = (v) => { const n = String(v ?? "").replace(/\D/g, ""); return n ? n.slice(-2).padStart(2, "0") : ""; };

export default function UsuariosPermisos() {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permisos, setPermisos] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [ups, setUps] = useState([]);
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState(EMPTY);
  const [matrix, setMatrix] = useState({});
  const [saving, setSaving] = useState(false);
  const [terr, setTerr] = useState([]);
  const [zonaEstado, setZonaEstado] = useState("");
  const [zonaMun, setZonaMun] = useState("");

  const load = async () => {
    const [u, r, p, m, up, t] = await Promise.all([
      api.get("/users"), api.get("/roles"), api.get("/permisos"), api.get("/modulos"), api.get("/usuario-permisos"), api.get("/territorial"),
    ]);
    setUsers(u.data || []); setRoles(r.data || []); setPermisos(p.data || []); setModulos(m.data || []); setUps(up.data || []); setTerr(t.data || []);
  };
  const loadTerr = async () => { const { data } = await api.get("/territorial"); setTerr(data || []); };
  const agregarZona = async () => {
    if (!zonaEstado || !zonaMun.trim()) return toast.error("Selecciona estado y escribe municipio");
    try {
      await api.post("/territorial", { estado: zonaEstado, municipio: zonaMun.trim(), vendedor_id: cur.id });
      setZonaMun("");
      loadTerr();
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };
  const quitarZona = async (id) => {
    try { await api.delete(`/territorial/${id}`); setTerr((p) => p.filter((x) => x.id !== id)); }
    catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };
  const estadoPrincipal = (uid) => {
    const zonas = terr.filter((t) => t.vendedor_id === uid);
    if (!zonas.length) return "—";
    const cnt = {};
    zonas.forEach((z) => { cnt[z.estado] = (cnt[z.estado] || 0) + 1; });
    return Object.entries(cnt).sort((a, b) => b[1] - a[1])[0][0];
  };
  useEffect(() => { load(); }, []);

  const nextConsecutivo = () => {
    const max = users.reduce((m, u) => {
      const n = parseInt(String(u.numero_identificacion || "").replace(/\D/g, ""), 10);
      return Number.isFinite(n) ? Math.max(m, n) : m;
    }, 0);
    return fmtId(String(max + 1));
  };
  const openNew = () => { setCur({ ...EMPTY, numero_identificacion: nextConsecutivo() }); setMatrix({}); setOpen(true); };
  const openEdit = (u) => {
    setCur({ id: u.id, full_name: u.full_name || "", username: u.username, email: u.email || "", password: "", rol: u.rol, celular: u.celular || "", numero_identificacion: fmtId(u.numero_identificacion), permite_sin_documentos: !!u.permite_sin_documentos });
    const mx = {};
    ups.filter((x) => x.profile_id === u.id).forEach((x) => { mx[x.modulo_id] = { ...(mx[x.modulo_id] || {}), [x.permiso_id]: true }; });
    setMatrix(mx);
    setOpen(true);
  };
  const toggle = (mId, pId, ch) => setMatrix((prev) => ({ ...prev, [mId]: { ...(prev[mId] || {}), [pId]: ch } }));

  const guardar = async () => {
    if (!cur.full_name || !cur.username || (!cur.id && !cur.password)) return toast.error("Nombre completo, usuario y contraseña son obligatorios");
    setSaving(true);
    const numId = fmtId(cur.numero_identificacion);
    try {
      let userId = cur.id;
      if (cur.id) {
        await api.put(`/users/${cur.id}`, { username: cur.username, full_name: cur.full_name, email: cur.email || null, rol: cur.rol, celular: cur.celular, numero_identificacion: numId, permite_sin_documentos: !!cur.permite_sin_documentos, ...(cur.password ? { password: cur.password } : {}) });
      } else {
        const { data } = await api.post("/users", { username: cur.username, password: cur.password, email: cur.email || null, rol: cur.rol, celular: cur.celular, numero_identificacion: numId, full_name: cur.full_name });
        userId = data.id;
      }
      const permisosList = [];
      Object.entries(matrix).forEach(([mId, perms]) => Object.entries(perms).forEach(([pId, on]) => { if (on) permisosList.push({ modulo_id: mId, permiso_id: pId }); }));
      await api.put(`/users/${userId}/permisos`, { permisos: permisosList });
      toast.success("Usuario guardado");
      setOpen(false); load();
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
    finally { setSaving(false); }
  };

  const eliminar = async (id) => {
    if (id === me.id) return toast.error("No puede eliminar su propio usuario");
    if (!window.confirm("¿Eliminar este usuario?")) return;
    try { await api.delete(`/users/${id}`); setUsers((p) => p.filter((u) => u.id !== id)); toast.success("Usuario eliminado"); }
    catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };

  const copiarPass = async (pwd) => {
    try {
      await navigator.clipboard.writeText(pwd);
      toast.success("Contraseña copiada");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const mover = async (idx, dir) => {
    const dest = idx + dir;
    if (dest < 0 || dest >= users.length) return;
    const nuevo = [...users];
    [nuevo[idx], nuevo[dest]] = [nuevo[dest], nuevo[idx]];
    setUsers(nuevo);
    try {
      await api.put("/users-orden", { ids: nuevo.map((u) => u.id) });
    } catch (err) {
      toast.error("No se pudo guardar el orden");
      load();
    }
  };

  const toggleBloqueo = async (u) => {
    if (u.id === me.id) return toast.error("No puede bloquear su propio usuario");
    const nuevo = (u.estatus || "activo") === "activo" ? "inactivo" : "activo";
    try {
      await api.put(`/users/${u.id}`, { estatus: nuevo });
      toast.success(nuevo === "inactivo" ? "Usuario bloqueado" : "Usuario desbloqueado");
      load();
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="usuarios-page">
      <Button variant="ghost" size="sm" onClick={() => navigate("/configuracion")}><ArrowLeft className="h-4 w-4 mr-2" /> Configuración</Button>
      <div className="flex justify-between items-center">
        <div><h1 className="text-3xl font-bold tracking-tight">Usuarios y Permisos</h1><p className="text-muted-foreground mt-2">Gestión de accesos por módulo.</p></div>
        <Button onClick={openNew} data-testid="nuevo-usuario-btn"><Plus className="h-4 w-4 mr-2" /> Nuevo Usuario</Button>
      </div>
      <Card>
        <CardContent className="pt-6">
          <div className="border rounded-md overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Usuario</TableHead><TableHead>Nombre</TableHead><TableHead>Estado principal</TableHead><TableHead>Rol</TableHead><TableHead>ID</TableHead><TableHead>Contraseña</TableHead><TableHead>Estatus</TableHead><TableHead>Último acceso</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
              <TableBody>
                {users.map((u, idx) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{u.username}</TableCell>
                    <TableCell>{nombreCorto(u.full_name)}</TableCell>
                    <TableCell data-testid={`user-estado-${u.id}`}>{estadoPrincipal(u.id)}</TableCell>
                    <TableCell className="capitalize">{u.rol}</TableCell>
                    <TableCell className="font-mono text-xs">{fmtId(u.numero_identificacion) || "-"}</TableCell>
                    <TableCell className="font-mono text-xs" data-testid={`user-pass-${u.id}`}>
                      {u.password_plain ? (
                        <div className="flex items-center gap-1.5">
                          <span>{u.password_plain}</span>
                          <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" title="Copiar contraseña" onClick={() => copiarPass(u.password_plain)} data-testid={`copy-pass-${u.id}`}>
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <span className="text-muted-foreground italic">sin registro</span>
                      )}
                    </TableCell>
                    <TableCell><Badge variant={u.estatus === "activo" ? "default" : "secondary"}>{u.estatus || "activo"}</Badge></TableCell>
                    <TableCell className="text-xs whitespace-nowrap" data-testid={`user-acceso-${u.id}`}>{u.ultimo_acceso ? fmtAcceso(u.ultimo_acceso) : <span className="text-muted-foreground italic">Nunca</span>}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" disabled={idx === 0} onClick={() => mover(idx, -1)} title="Subir" data-testid={`user-up-${u.id}`}><ChevronUp className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" disabled={idx === users.length - 1} onClick={() => mover(idx, 1)} title="Bajar" data-testid={`user-down-${u.id}`}><ChevronDown className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className={(u.estatus || "activo") === "activo" ? "text-destructive" : "text-green-600"} onClick={() => toggleBloqueo(u)} title={(u.estatus || "activo") === "activo" ? "Bloquear acceso" : "Desbloquear acceso"} data-testid={`user-block-${u.id}`}>{(u.estatus || "activo") === "activo" ? <Ban className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}</Button>
                        <Button variant="outline" size="icon" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="destructive" size="icon" onClick={() => eliminar(u.id)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{cur.id ? "Editar usuario" : "Nuevo usuario"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 sm:col-span-2"><Label>Nombre completo *</Label><Input value={cur.full_name} onChange={(e) => setCur({ ...cur, full_name: e.target.value })} data-testid="user-fullname" placeholder="Ej. Andrés García" /></div>
            <div className="space-y-2"><Label>Usuario *</Label><Input value={cur.username} onChange={(e) => setCur({ ...cur, username: e.target.value })} data-testid="user-username" /></div>
            <div className="space-y-2"><Label>Contraseña {cur.id ? "(dejar vacío para no cambiar)" : "*"}</Label><Input type="password" value={cur.password} onChange={(e) => setCur({ ...cur, password: e.target.value })} data-testid="user-password" /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={cur.email} onChange={(e) => setCur({ ...cur, email: e.target.value })} /></div>
            <div className="space-y-2"><Label>Celular</Label><Input value={cur.celular} onChange={(e) => setCur({ ...cur, celular: e.target.value })} /></div>
            <div className="space-y-2"><Label>ID (asignado automáticamente)</Label><Input value={cur.numero_identificacion} readOnly disabled className="bg-muted" data-testid="user-numid" placeholder="Automático" /></div>
            <div className="space-y-2">
              <Label>Rol</Label>
              <Select value={cur.rol} onValueChange={(v) => setCur({ ...cur, rol: v })}>
                <SelectTrigger data-testid="user-rol"><SelectValue /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r.id} value={r.nombre}>{r.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2 flex items-center gap-2 bg-muted/50 p-3 rounded-md">
              <Checkbox checked={!!cur.permite_sin_documentos} onCheckedChange={(ch) => setCur({ ...cur, permite_sin_documentos: !!ch })} data-testid="user-sin-docs" />
              <Label className="cursor-pointer text-sm">Puede enviar solicitudes de alta sin documentos (para migrar clientes existentes)</Label>
            </div>
          </div>
          <div className="mt-4">
            <h4 className="font-semibold mb-2">Matriz de permisos</h4>
            <div className="border rounded-md overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Módulo</TableHead>{permisos.map((p) => <TableHead key={p.id} className="text-center">{p.nombre}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                  {modulos.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className="font-medium">{m.nombre}</TableCell>
                      {permisos.map((p) => (
                        <TableCell key={p.id} className="text-center">
                          <Checkbox checked={!!matrix[m.id]?.[p.id]} onCheckedChange={(ch) => toggle(m.id, p.id, ch)} data-testid={`perm-${m.id}-${p.id}`} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          {cur.id && cur.rol === "vendedor" && (
            <div className="mt-6" data-testid="zonas-venta">
              <h4 className="font-semibold mb-2">Zonas de venta</h4>
              <div className="flex flex-col sm:flex-row gap-2">
                <Select value={zonaEstado} onValueChange={setZonaEstado}>
                  <SelectTrigger className="sm:w-64" data-testid="zona-estado"><SelectValue placeholder="Estado" /></SelectTrigger>
                  <SelectContent>{ESTADOS_MX.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                </Select>
                <Input className="sm:flex-1" placeholder="Municipio" value={zonaMun} onChange={(e) => setZonaMun(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); agregarZona(); } }} data-testid="zona-municipio" />
                <Button type="button" onClick={agregarZona} data-testid="zona-add"><Plus className="h-4 w-4 mr-1" /> Agregar</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {terr.filter((t) => t.vendedor_id === cur.id).length === 0 ? (
                  <span className="text-sm text-muted-foreground">Sin zonas asignadas.</span>
                ) : terr.filter((t) => t.vendedor_id === cur.id).map((t) => (
                  <Badge key={t.id} variant="secondary" className="gap-1 pr-1" data-testid={`zona-${t.id}`}>
                    {t.estado} · {t.municipio}
                    <button type="button" onClick={() => quitarZona(t.id)} className="ml-1 hover:text-destructive" title="Quitar"><X className="h-3 w-3" /></button>
                  </Badge>
                ))}
              </div>
            </div>
          )}
          {cur.id && (
            <div className="mt-6">
              <h4 className="font-semibold mb-2">Documentos</h4>
              <DocumentosSection entidadTipo="usuario" entidadId={cur.id} canManage={true} tipos={["INE", "Comprobante de domicilio", "Contrato firmado", "Constancia de situación fiscal (RFC)", "Currículum"]} />
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={guardar} disabled={saving} data-testid="user-save">{saving ? "Guardando..." : "Guardar"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
